import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Icon } from '@iconify/react'
import { subscribeToChatLoading } from '@/pages/dashboard/chat/ChatHistoryPage.jsx'
import { Button } from '@/components/ui/button.jsx'
import { Input } from '@/components/ui/input.jsx'
import { TypographyXSmallMuted } from '@/components/ui/typography'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.jsx'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog.jsx'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu.jsx'
import { toast } from 'sonner'
import * as API from '@/services/api.js'
import { handleError } from '@/services/errorHandler.js'
import { AnalyticsService } from '@/services/analytics.js'
import { AdAccountSelect } from '@/components/ad-account-select/AdAccountSelect.jsx'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { useCreativeAiChat } from '@/context-state/CreativeAiChatContext.jsx'

const formatRelativeDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) {
        return 'Just now'
    }
    if (diffMins < 60) {
        return `${diffMins}m ago`
    }
    if (diffHours < 24) {
        return `${diffHours}h ago`
    }
    if (diffDays < 7) {
        return `${diffDays}d ago`
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Individual chat item with hover 3-dot menu and inline rename
const ChatItem = ({ chatSession, isChosen, onClick, projects, onRefresh, onDeleteChosen, instructions = [] }) => {
    const [isRenaming, setIsRenaming] = useState(false)
    const [renameValue, setRenameValue] = useState(chatSession.title)
    const [isActionLoading, setIsActionLoading] = useState(false)
    const [isChatThinking, setIsChatThinking] = useState(false)
    const renameInputRef = useRef(null)

    // Subscribe to per-chat loading broadcasts
    useEffect(() => {
        return subscribeToChatLoading((loadingChatId, loading) => {
            if (loadingChatId === chatSession._id) {
                setIsChatThinking(loading)
            }
        })
    }, [chatSession._id])

    useEffect(() => {
        if (isRenaming && renameInputRef.current) {
            renameInputRef.current.focus()
            renameInputRef.current.select()
        }
    }, [isRenaming])

    const handleRename = async () => {
        const trimmed = renameValue.trim()
        if (!trimmed || trimmed === chatSession.title) {
            setIsRenaming(false)
            setRenameValue(chatSession.title)
            return
        }
        try {
            setIsActionLoading(true)
            await API.updateChat(chatSession._id, { title: trimmed })
            onRefresh()
            toast.success('Chat renamed')
            AnalyticsService.track('chat-renamed', { chatId: chatSession._id })
        } catch (e) {
            handleError(e)
            toast.error('Failed to rename chat')
            setRenameValue(chatSession.title)
        } finally {
            setIsActionLoading(false)
        }
        setIsRenaming(false)
    }

    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleRename()
        }
        if (e.key === 'Escape') {
            setIsRenaming(false)
            setRenameValue(chatSession.title)
        }
    }

    const handleDelete = async () => {
        try {
            setIsActionLoading(true)
            await API.deleteChat(chatSession._id)
            if (isChosen && onDeleteChosen) {
                onDeleteChosen()
            }
            onRefresh()
            toast.success('Chat deleted')
            AnalyticsService.track('chat-deleted', { chatId: chatSession._id })
        } catch (e) {
            handleError(e)
            toast.error('Failed to delete chat')
            setIsActionLoading(false)
        }
    }

    const handleMoveToProject = async (projectId) => {
        try {
            setIsActionLoading(true)
            await API.updateChat(chatSession._id, { projectId })
            onRefresh()
            toast.success(projectId ? 'Moved to folder' : 'Removed from folder')
            AnalyticsService.track('chat-moved-to-folder', { chatId: chatSession._id, projectId })
        } catch (e) {
            handleError(e)
            toast.error('Failed to move chat')
        } finally {
            setIsActionLoading(false)
        }
    }

    return (
        <div
            className={`group/chat flex items-center gap-1 relative transition-colors cursor-pointer px-3 py-2 rounded-lg ${isChosen ? 'bg-primary/10 text-primary' : 'hover:bg-accent'} ${isActionLoading ? 'opacity-50 pointer-events-none' : ''}`}
            onClick={() => {
                if (!isRenaming && !isActionLoading) {
                    onClick()
                }
            }}
        >
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                {isRenaming ? (
                    <Input
                        ref={renameInputRef}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={handleRename}
                        onKeyDown={handleRenameKeyDown}
                        className="h-6 text-sm px-1 py-0"
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <div className="flex items-center gap-1.5">
                        <span className="text-sm font-light truncate">{chatSession.title}</span>
                        {isChatThinking && (
                            <Icon icon="svg-spinners:3-dots-fade" className="h-3.5 w-3.5 text-primary shrink-0" />
                        )}
                    </div>
                )}
                <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{formatRelativeDate(chatSession.createdAt)}</span>
                    {chatSession.instructionId && (() => {
                        const instruction = instructions.find(i => (i.id || i._id) === chatSession.instructionId)
                        if (!instruction) {
                            return null
                        }
                        return (
                            <span className="inline-flex items-center gap-0.5 rounded border border-primary/30 bg-primary/5 px-1 py-px">
                                <Icon icon="ph:list-checks" className="h-2.5 w-2.5 text-primary" />
                                <span className="text-[10px] font-medium text-primary max-w-[80px] truncate">{instruction.name}</span>
                            </span>
                        )
                    })()}
                </div>
            </div>

            {/* 3-dot menu — always in DOM, shown via CSS on hover or when chosen */}
            {!isRenaming && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className={`h-6 w-6 shrink-0 transition-opacity ${isChosen ? 'opacity-100' : 'opacity-0 group-hover/chat:opacity-100'}`}>
                            <Icon icon="mdi:dots-horizontal" className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation()
                            setIsRenaming(true)
                        }}>
                            <Icon icon="mdi:pencil-outline" className="h-4 w-4 mr-2" />
                            Rename
                        </DropdownMenuItem>

                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                                <Icon icon="ph:arrow-square-right" className="h-4 w-4 mr-2" />
                                Move to folder
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-48">
                                {chatSession.projectId && (
                                    <>
                                        <DropdownMenuItem onClick={(e) => {
                                            e.stopPropagation()
                                            handleMoveToProject(null)
                                        }}>
                                            <Icon icon="ph:arrow-square-left" className="h-4 w-4 mr-2" />
                                            Remove from folder
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}
                                {projects.map(project => (
                                    <DropdownMenuItem
                                        key={project._id}
                                        disabled={chatSession.projectId === project._id}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleMoveToProject(project._id)
                                        }}
                                    >
                                        <Icon icon="ph:stack" className="h-4 w-4 mr-2" />
                                        {project.name}
                                    </DropdownMenuItem>
                                ))}
                                {projects.length > 0 && <DropdownMenuSeparator />}
                                <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation()
                                    window.dispatchEvent(new CustomEvent('create-project-for-chat', { detail: { chatId: chatSession._id } }))
                                }}>
                                    <Icon icon="ph:stack-plus" className="h-4 w-4 mr-2" />
                                    Create new folder
                                </DropdownMenuItem>
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>

                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleDelete()
                            }}
                        >
                            <Icon icon="mdi:trash-can-outline" className="h-4 w-4 mr-2" />
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    )
}

// Project accordion header with inline rename and 3-dot menu
const ProjectHeader = ({ project, onRefresh }) => {
    const [isRenaming, setIsRenaming] = useState(false)
    const [renameValue, setRenameValue] = useState(project.name)
    const [isActionLoading, setIsActionLoading] = useState(false)
    const renameInputRef = useRef(null)

    useEffect(() => {
        if (isRenaming && renameInputRef.current) {
            renameInputRef.current.focus()
            renameInputRef.current.select()
        }
    }, [isRenaming])

    const handleRename = async () => {
        const trimmed = renameValue.trim()
        if (!trimmed || trimmed === project.name) {
            setIsRenaming(false)
            setRenameValue(project.name)
            return
        }
        try {
            setIsActionLoading(true)
            await API.renameProject(project._id, { name: trimmed })
            onRefresh()
            toast.success('Folder renamed')
            AnalyticsService.track('folder-renamed', { projectId: project._id })
        } catch (e) {
            handleError(e)
            toast.error('Failed to rename folder')
            setRenameValue(project.name)
        } finally {
            setIsActionLoading(false)
        }
        setIsRenaming(false)
    }

    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleRename()
        }
        if (e.key === 'Escape') {
            setIsRenaming(false)
            setRenameValue(project.name)
        }
    }

    const handleDelete = async () => {
        try {
            setIsActionLoading(true)
            await API.deleteProject(project._id)
            onRefresh()
            toast.success('Folder deleted')
            AnalyticsService.track('folder-deleted', { projectId: project._id })
        } catch (e) {
            handleError(e)
            toast.error('Failed to delete folder')
            setIsActionLoading(false)
        }
    }

    if (isRenaming) {
        return (
            <div className="flex items-center gap-2 w-full" onClick={(e) => e.stopPropagation()}>
                <Icon icon="ph:stack" className="h-4 w-4 shrink-0" />
                <Input
                    ref={renameInputRef}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={handleRename}
                    onKeyDown={handleRenameKeyDown}
                    className="h-6 text-sm px-1 py-0 flex-1 min-w-0"
                />
            </div>
        )
    }

    return (
        <div className={`flex items-center gap-2 group/project flex-1 min-w-0 ${isActionLoading ? 'opacity-50' : ''}`}>
            <Icon icon="ph:stack" className="h-4 w-4 shrink-0" />
            <span className="text-sm font-light truncate flex-1 text-left">{project.name}</span>
            <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 opacity-0 group-hover/project:opacity-100 transition-opacity">
                        <Icon icon="mdi:dots-horizontal" className="h-3.5 w-3.5" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation()
                        setIsRenaming(true)
                    }}>
                        <Icon icon="mdi:pencil-outline" className="h-4 w-4 mr-2" />
                        Rename
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleDelete()
                        }}
                    >
                        <Icon icon="mdi:trash-can-outline" className="h-4 w-4 mr-2" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export const ChatSubNavbar = ({ isFullScreenMode = false, onSelectChat = null, onNewChat = null, activeChatId: externalActiveChatId = null }) => {

    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { chatId: routeActiveChatId, accountId, spaceId } = useParams()
    const activeChatId = isFullScreenMode ? externalActiveChatId : routeActiveChatId
    const dashboardBase = `/account/${accountId}/space/${spaceId}/dashboard`
    const dashboardContext = useDashboardContext()
    const chosenAdAccount = dashboardContext?.adAccountsContext?.chosenAdAccount
    const { aiSettings } = useCreativeAiChat()

    const instructions = aiSettings.instructions
    const knowledge = aiSettings.knowledge
    const hasBrandContext = aiSettings.hasBrandContext

    const [chats, setChats] = useState([])
    const [projects, setProjects] = useState([])
    const [isFetching, setIsFetching] = useState(false)
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false)

    // Create project dialog state
    const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false)
    const [newProjectName, setNewProjectName] = useState('')
    const [pendingChatIdForProject, setPendingChatIdForProject] = useState(null)

    // Fetch chats on every navigation / ad account change
    useEffect(() => {
        fetchChats()
    }, [pathname, externalActiveChatId, chosenAdAccount])

    // Fetch projects once on mount
    useEffect(() => {
        fetchProjects()
    }, [])

    // Listen for "create project for chat" events from the dropdown menu
    useEffect(() => {
        const handler = (e) => {
            setPendingChatIdForProject(e.detail.chatId)
            setIsCreateProjectDialogOpen(true)
        }
        window.addEventListener('create-project-for-chat', handler)
        return () => window.removeEventListener('create-project-for-chat', handler)
    }, [])

    const fetchChats = async () => {
        try {
            setIsFetching(true)
            const { data } = await API.getUserChats(chosenAdAccount)
            setChats(data || [])
        } catch (e) {
            handleError(e)
        } finally {
            setIsFetching(false)
            setHasFetchedOnce(true)
        }
    }

    const fetchProjects = async () => {
        try {
            const { data } = await API.getUserProjects()
            setProjects(data || [])
        } catch (e) {
            handleError(e)
        }
    }

    // Refresh both after mutations (create/delete project, move chat)
    const refreshAfterMutation = async () => {
        await Promise.all([fetchChats(), fetchProjects()])
    }

    const goToNewChat = () => {
        AnalyticsService.track('chat-new-chat-clicked')
        if (isFullScreenMode && onNewChat) {
            onNewChat()
        } else {
            navigate('chat')
        }
    }

    const goToChat = (chatSessionId) => {
        AnalyticsService.track('chat-history-item-clicked', { chatId: chatSessionId })
        if (isFullScreenMode && onSelectChat) {
            onSelectChat(chatSessionId)
        } else {
            navigate(`chat/${chatSessionId}`)
        }
    }

    const handleCreateProjectDialogClose = () => {
        setIsCreateProjectDialogOpen(false)
        setNewProjectName('')
        setPendingChatIdForProject(null)
    }

    const handleCreateProject = async () => {
        const trimmed = newProjectName.trim()
        if (!trimmed) {
            return
        }
        try {
            const response = await API.createProject({ name: trimmed })
            const newProject = response.data

            // If a chat was queued to move into this project, move it now
            if (pendingChatIdForProject) {
                await API.updateChat(pendingChatIdForProject, { projectId: newProject._id })
            }

            handleCreateProjectDialogClose()
            refreshAfterMutation()
            toast.success('Folder created')
            AnalyticsService.track('folder-created', { name: trimmed })
        } catch (e) {
            handleError(e)
            toast.error('Failed to create folder')
        }
    }

    // Split chats into ungrouped and per-project
    const ungroupedChats = chats.filter(c => !c.projectId)
    const chatsByProject = {}
    for (const chat of chats) {
        if (chat.projectId) {
            if (!chatsByProject[chat.projectId]) {
                chatsByProject[chat.projectId] = []
            }
            chatsByProject[chat.projectId].push(chat)
        }
    }

    // Auto-open the project that contains the active chat
    const activeChat = activeChatId ? chats.find(c => c._id === activeChatId) : null
    const defaultOpenProjects = activeChat?.projectId ? [activeChat.projectId] : []

    return (
        <div className="pt-6 flex flex-col h-full">

            {/* Ad Account Selector */}
            <div className="px-3 mb-3">
                <AdAccountSelect />
            </div>

            {/* New Chat button */}
            <motion.div
                className="px-3 mb-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
            >
                <Button
                    onClick={goToNewChat}
                    className="w-full gap-2"
                    size="sm"
                >
                    <Icon icon="ic:baseline-plus" className="h-4 w-4" />
                    New Chat
                </Button>
            </motion.div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-2 space-y-2">
                {!hasFetchedOnce && isFetching && (
                    <TypographyXSmallMuted className="text-center py-4">
                        Loading...
                    </TypographyXSmallMuted>
                )}

                {/* AI Settings */}
                {hasFetchedOnce && (
                    <motion.div
                        className="space-y-1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                    >
                        {[
                            { label: 'Brand Context', icon: 'ph:globe-simple', path: `${dashboardBase}/chat/settings/context`, missing: !hasBrandContext },
                            { label: 'Knowledge', icon: 'ph:book-open', path: `${dashboardBase}/chat/settings/knowledge`, missing: knowledge.length === 0 },
                            { label: 'Instructions', icon: 'ph:list-checks', path: `${dashboardBase}/chat/settings/instructions` },
                            { label: 'Preferences', icon: 'ph:sliders', path: `${dashboardBase}/chat/settings/preferences` },
                        ].map((item, index) => (
                            <motion.div
                                key={item.path}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.15 + 0.03 * index, duration: 0.2 }}
                                className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg transition-colors ${pathname.includes(item.path) ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                                onClick={() => {
                                    AnalyticsService.track('chat-ai-settings-clicked', { label: item.label })
                                    navigate(item.path)
                                }}
                            >
                                <Icon icon={item.icon} className="h-4 w-4" />
                                <span className="text-sm font-light">{item.label}</span>
                                {item.missing && (
                                    <span className="ml-auto text-[10px] font-medium text-amber-600 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded">
                                        missing
                                    </span>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                )}

                {hasFetchedOnce && <div className="h-px bg-border my-2 mx-2" />}

                {/* Create Folder button */}
                {hasFetchedOnce && (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.2 }}
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            className="gap-2 hover:bg-primary/10 w-full justify-start pl-2"
                            onClick={() => setIsCreateProjectDialogOpen(true)}
                        >
                            <Icon icon="ic:baseline-plus" className="h-6 w-6 rounded-full bg-primary/10 p-1 text-primary" />
                            Create Folder
                        </Button>
                    </motion.div>
                )}

                {/* Project accordions */}
                {projects.length > 0 && (
                    <Accordion type="multiple" defaultValue={defaultOpenProjects} className="w-full space-y-1">
                        {projects.map(project => {
                            const projectChats = chatsByProject[project._id] || []
                            return (
                                <motion.div
                                    key={project._id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <AccordionItem value={project._id} className="border-0">
                                        <AccordionTrigger className="px-3 py-2 hover:no-underline">
                                            <ProjectHeader project={project} onRefresh={refreshAfterMutation} />
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="space-y-1 px-1 py-1">
                                                {projectChats.length === 0 && (
                                                    <TypographyXSmallMuted className="text-center py-2">
                                                        No chats in this folder
                                                    </TypographyXSmallMuted>
                                                )}
                                                {projectChats.map((chatSession, index) => (
                                                    <motion.div
                                                        key={chatSession._id}
                                                        className="relative"
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: 0.03 * index, duration: 0.2 }}
                                                    >
                                                        <ChatItem
                                                            chatSession={chatSession}
                                                            isChosen={chatSession._id === activeChatId}
                                                            onClick={() => goToChat(chatSession._id)}
                                                            projects={projects}
                                                            onRefresh={refreshAfterMutation}
                                                            onDeleteChosen={() => isFullScreenMode && onNewChat ? onNewChat() : navigate('chat')}
                                                            instructions={instructions}
                                                        />
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </motion.div>
                            )
                        })}
                    </Accordion>
                )}

                {projects.length > 0 && <div className="h-px bg-border my-2 mx-2" />}

                {/* Ungrouped chats */}
                {ungroupedChats.map((chatSession, index) => (
                    <motion.div
                        key={chatSession._id}
                        className="relative"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.03 * index, duration: 0.2 }}
                    >
                        <ChatItem
                            chatSession={chatSession}
                            isChosen={chatSession._id === activeChatId}
                            onClick={() => goToChat(chatSession._id)}
                            projects={projects}
                            onRefresh={refreshAfterMutation}
                            onDeleteChosen={() => isFullScreenMode && onNewChat ? onNewChat() : navigate('chat')}
                            instructions={instructions}
                        />
                    </motion.div>
                ))}

                {hasFetchedOnce && chats.length === 0 && (
                    <TypographyXSmallMuted className="text-center py-4">
                        No previous chats yet
                    </TypographyXSmallMuted>
                )}
            </div>

            {/* Create Folder Dialog */}
            <Dialog open={isCreateProjectDialogOpen} onOpenChange={setIsCreateProjectDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New Folder</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Input
                            placeholder="Project name"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleCreateProject()
                                }
                            }}
                            className="w-full"
                            autoFocus
                        />
                    </div>
                    <DialogFooter className="flex justify-between sm:justify-between">
                        <Button variant="outline" onClick={handleCreateProjectDialogClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateProject} disabled={!newProjectName.trim()}>
                            Create
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
